import { ILayer } from '../layer.type';
import { environment } from '../../../../../environments/environment';
import { getCSSColorVariableValue } from '../../../utils';
import { _ } from '../../../utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { SymbolLayout, SymbolPaint } from 'mapbox-gl';

const markerUrlBase = environment.assetRoot + '/data/T13/markers/';

const pumpLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': 'top',
  'text-radial-offset': 1,
  'text-size': 10,
};

const pumpPaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
};

const smallIconLayout: SymbolLayout = {
  'text-size': [
    'interpolate', ['linear'], ['zoom'],
    0, 3,
    13, 12,
    16, 15,
  ],
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    0, 0.2,
    13, 0.4,
    16, 1.0,
  ],
};

/**
 * Joensuu
 */
export default (): TenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 62.598,
    lng: 29.783,
  },
  defaultZoom: 10.96,
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T13.json by MapService.addLayer
export function getLayers(): ILayer[] {
  return [
    {
      id: 'HP_havaintoputki',
      vectorSourceLayer: 'HP_havaintoputki',
      name: 'Havaintoputket',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Havaintoputki.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: smallIconLayout,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'JVL_jvpuhdistamo',
      vectorSourceLayer: 'JVL_jvpuhdistamo',
      name: 'Jätevedenpuhdistamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'jätevesilaitos4.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'JVP_jvpumppaamo',
      vectorSourceLayer: 'JVP_jvpumppaamo',
      name: 'Jätevedenpumppaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...pumpLayout,
          ...smallIconLayout,
          'text-field': 'JVP',
        },
        paint: pumpPaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        group: 'Pumppaamot',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'MA_mittausasema',
      vectorSourceLayer: 'MA_mittausasema',
      name: 'Mittausasemat',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'mittausasema.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'VO_vedenottamo',
      vectorSourceLayer: 'VO_vedenottamo',
      name: 'Vedenottamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Vedenottamo.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'VT_vesitorni',
      vectorSourceLayer: 'VT_vesitorni',
      name: 'Vesitornit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Vesitorni.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'Ilmakello',
      vectorSourceLayer: 'Ilmakello',
      name: 'Ilmauskellot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Ilmakello.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'VL_vesilaitos',
      vectorSourceLayer: 'VL_vesilaitos',
      name: 'Vesilaitokset',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Vesilaitos2.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'HVP_hulevesipumppaamo',
      vectorSourceLayer: 'HVP_hulevesipumppaamo',
      name: 'Hulevesipumpaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...pumpLayout,
          ...smallIconLayout,
          'text-field': 'HVP',
        },
        paint: pumpPaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        group: 'Pumppaamot',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'PK_paineenkorottamo',
      vectorSourceLayer: 'PK_paineenkorottamo',
      name: 'Paineenkorottamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...pumpLayout,
          ...smallIconLayout,
          'text-field': 'PK',
        },
        paint: pumpPaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        group: 'Pumppaamot',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'TA_tukiasema',
      vectorSourceLayer: 'TA_tukiasema',
      name: 'Tukiasemat',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Tukiasema.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'PP_Paloposti',
      vectorSourceLayer: 'PP_Paloposti',
      name: 'Palopostit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Paloposti.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'Naytteenotto',
      vectorSourceLayer: 'Naytteenotto',
      name: 'Näytteenottopisteet',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Näytteenottopiste.png',
        },
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    //
    // LINE DATA STARTS HERE
    //
    {
      id: 'JNS_wastewater_network',
      name: _('Wastewater network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#cf923b',
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_clean_water_network',
      name: _('Clean water network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('info-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_subsurface_drain_network',
      name: _('Subsurface drain network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JNS_rainwater_network',
      name: _('Urban runoff network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'VOK_clean_water_network',
      name: _('VOK clean water network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'VOK_wastewater_network',
      name: _('VOK wastewater network'),
      orderingWeight: 0,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: getCSSColorVariableValue('success-base'),
        strokeWeight: 1.5,
        visible: false,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    getSharedLayer('houseNumbers'),
    getSharedLayer('contour'),
    getSharedLayer('depthContour'),
    getSharedLayer('groundWaterAreas'),
    getSharedLayer('drainageAreas'),
    getSharedLayer('basinOutlets'),
  ];
}
